// <copy-field> — click-to-copy wrapper for payment details. Markup
// convention (rendered by Views::Invoices::Show#copy_field):
//
//   <copy-field data-value="30995105" role="button" tabindex="0" aria-label="Copy Account number">
//     …visible label/value…
//     (a "Copied" chip styled via group-data-[copied]:opacity-100)
//   </copy-field>
//
// Click, Enter, or Space copies data-value and sets data-copied on the host
// for 1.2s — feedback is pure CSS off that attribute, so the element stays
// dumb about presentation. navigator.clipboard needs a secure context
// (https or localhost); the execCommand path covers plain-http dev hosts.

class CopyField extends HTMLElement {
	#abort: AbortController | null = null;
	#timer: number | null = null;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.addEventListener("click", () => this.#copy(), { signal });
		this.addEventListener(
			"keydown",
			(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					this.#copy();
				}
			},
			{ signal },
		);
	}

	disconnectedCallback() {
		this.#abort?.abort();
		if (this.#timer !== null) window.clearTimeout(this.#timer);
	}

	async #copy() {
		const value = this.dataset.value;
		if (!value) return;

		try {
			await navigator.clipboard.writeText(value);
		} catch {
			this.#copyFallback(value);
		}

		this.#flash();
	}

	#copyFallback(value: string) {
		const textarea = document.createElement("textarea");
		textarea.value = value;
		textarea.style.position = "fixed";
		textarea.style.opacity = "0";
		document.body.append(textarea);
		textarea.select();
		document.execCommand("copy");
		textarea.remove();
	}

	#flash() {
		this.setAttribute("data-copied", "");
		if (this.#timer !== null) window.clearTimeout(this.#timer);
		this.#timer = window.setTimeout(
			() => this.removeAttribute("data-copied"),
			1200,
		);
	}
}

if (!customElements.get("copy-field")) {
	customElements.define("copy-field", CopyField);
}
